import { useState, useRef, useEffect } from "react";
import { Box, Typography, Paper, Grid, Button, Popover } from "@mui/material";
import CachedOutlinedIcon from '@mui/icons-material/CachedOutlined';
import { IDashboardBuildingDTO, IDashboardStats } from "../../interfaces/Dashboard";
import { IOrderStatsDTO } from "../../interfaces/Delivery";
import { getDashboardBuildingCapacity, getDashboardStats } from "../../services/Dashboard";
import { useAuth } from "../../auth/AuthProvider";
import { getDeliveryStats } from "../../services/Delivery";

const getTopCards = (stats: IOrderStatsDTO | null) => [
  { label: "Orders in System", value: stats?.totalOrders ?? 0, change: 5.2, trend: "up", subtext: "Then yesterday" },
  { label: "Orders Backlog", value: stats?.unPacked ?? 0, change: 120, trend: "up", subtext: "Item" },
  { label: "Orders Delivered", value: stats?.delivered ?? 0, change: 8.2, trend: "up", subtext: "Then Yesterday" },
];

const getBottomCards = (cardStats: IDashboardStats | null) => [
  { label: "Warehouse Capacity Used",value: `${Number.isInteger(cardStats?.occupiedPercentage ?? 0) ? (cardStats?.occupiedPercentage ?? 0) : (cardStats?.occupiedPercentage ?? 0).toFixed(2)}%`, change: null, trend: null, subtext: "Critical", critical: true },
  { label: "Items out of Stock", value: cardStats?.outOfStockItems ?? 0, change: null, trend: null, subtext: "Incidents critical", critical: true },
];

const Dashboard=() => {
  const { getLoginUserInfo } = useAuth();
  const loggedInUser = getLoginUserInfo();
  const [loading, setLoading] = useState(false);
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [selectedBuilding, setSelectedBuilding] = useState<any>(null);
  const [cardStats,setCardStats] = useState<IDashboardStats | null>(null);
  const [stats, setStats] = useState<IOrderStatsDTO | null>(null);
  const [buildingData, setBuildingData] = useState<Array<IDashboardBuildingDTO>>([]);
  const closeTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  const fetchStats = async () => {
    if (!loggedInUser?.workspaceId ) return;
    try {
      const res = await getDashboardStats(loggedInUser?.workspaceId);
      setCardStats(res ?? null);
    } catch {
      setCardStats(null);
    }
  };
  const fetchRemainingStat = async () => {
    try {
      const res = await getDeliveryStats();
      setStats(res ?? null);
    } catch {
      setStats(null);
    }
  };
  const fetchBuildingCapacity = async () => {
    if (!loggedInUser?.workspaceId ) return;
    try {
      setLoading(true);
      const buildingRes = await getDashboardBuildingCapacity(loggedInUser?.workspaceId);
      setBuildingData(Array.isArray(buildingRes) ? buildingRes : []);
    } catch {
      setBuildingData([]);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    fetchStats();
    fetchRemainingStat();
    fetchBuildingCapacity();
  }, []);
  const handleRefresh = async () => {
    fetchStats();
    fetchRemainingStat();
    fetchBuildingCapacity();
  };

  const handlePopoverOpen = (event: React.MouseEvent<HTMLElement>, building: any) => {
    if (closeTimeoutRef.current) {
      clearTimeout(closeTimeoutRef.current);
    }
    setAnchorEl(event.currentTarget);
    setSelectedBuilding(building);
  };

  const handlePopoverClose = () => {
    closeTimeoutRef.current = setTimeout(() => {
      setAnchorEl(null);
      setSelectedBuilding(null);
    }, 100);
  };

  const open = Boolean(anchorEl);

  return (
    <Box sx={{ p:1, height: "calc(100vh - 3.5rem)", overflow: "hidden" }}>
      <Paper sx={{ p:1, backgroundColor: "background.paper", borderRadius: 2, height: "100%", display: "flex", flexDirection: "column" }}>
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb:1}}>
          <Box>
            <Typography variant="body2" fontWeight={700} fontSize={"22px"}>Global Health Summary</Typography>
          </Box>
          <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
            <Typography variant="body2"  fontWeight={400} fontSize={"12px"} color="grey.500">Last updated: 08/01/2026</Typography>
            <Button
              variant="contained"
              startIcon={<CachedOutlinedIcon sx={{ animation: loading ? 'spin 1s linear infinite' : 'none', '@keyframes spin': { '0%': { transform: 'rotate(0deg)' }, '100%': { transform: 'rotate(360deg)' } } }} />}
              onClick={handleRefresh}
              sx={{
                borderRadius: "6px",
                border: "1px solid",
                borderColor: "primary.main",
                padding: "4px 10px",
                boxShadow:" 0 0 10.7px 0 rgba(0, 94, 255, 0.20) inset",
                textTransform: "none",
                "&:hover": {
                  backgroundColor: "primary.main",
                boxShadow:" 0 0 10.7px 0 rgba(0, 94, 255, 0.20) inset",

                },
                pointerEvents: loading ? 'none' : 'auto'
              }}
            >
              Refresh
            </Button>
          </Box>
        </Box>

        <Box sx={{ flex: 1, overflow: "auto", "&::-webkit-scrollbar": { width: "8px" }, "&::-webkit-scrollbar-track": { background: "transparent" }, "&::-webkit-scrollbar-thumb": { background: "#CBD5E1", borderRadius: "4px" } }}>

        <Grid container spacing={2}>
          {getTopCards(stats).map((item, index) =>(
            <Grid item xs={12} sm={6} md={4} key={index}>
              <Paper sx={{ p: 2, backgroundColor: "background.default", borderRadius: 2, height: "100%" }}>
                <Typography variant="body2" color="grey.700" sx={{ fontSize: "14px", fontWeight:"400", mb: 1 }}>
                  {item.label}
                </Typography>
                <Typography variant="h4" fontWeight={700}  fontSize="32px" color="#000" sx={{ mb: 1 }}>
                  {item.value}
                </Typography>
                <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                  <Typography variant="caption" sx={{ fontSize: "11px" }}>
                    <Typography component="span" color={item.trend === "up" ? "success.main" : "error.main"}>
                      {item.trend === "up" ? "+" : "-"}{item.change}%
                    </Typography>
                    <Typography component="span" color="grey.700" sx={{ ml: 0.5 , fontSize: "12px", fontWeight:"400"  }}>
                      {item.subtext}
                    </Typography>
                  </Typography>
                </Box>
              </Paper>
            </Grid>
          ))}
          {getBottomCards(cardStats).map((item, index) =>(
            <Grid item xs={12} sm={6} md={6} key={index}>
              <Paper sx={{ p: 2, backgroundColor: "background.default", borderRadius: 2, height: "100%" }}>
                <Typography variant="body2" color="grey.700" sx={{ fontSize: "14px", fontWeight:"400", mb: 1 }}>
                  {item.label}
                </Typography>
                <Typography variant="h4" fontWeight={700}  fontSize="32px" color="#000" sx={{ mb: 1 }}>
                  {item.value}
                </Typography>
                <Typography variant="caption" color={item.critical ? "error.main" : "grey.700"} sx={{ fontSize: "12px", fontWeight:"400" }}>
                  {item.subtext}
                </Typography>
              </Paper>
            </Grid>
          ))}
        </Grid>

        <Box sx={{ mt:2}}>
          <Typography  variant="body2" fontWeight={700} fontSize={"22px"} sx={{ mb: 1}}>Building Capacity</Typography>
          <Paper sx={{ p:2, backgroundColor: "background.default", borderRadius: 2 }}>
          <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 3, mb:2}}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <Box sx={{ width: 12, height: 12, borderRadius: "50%", bgcolor: "success.main" }} />
              <Typography variant="caption" fontSize= "16px" fontWeight="500">Available</Typography>
            </Box>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <Box sx={{ width: 12, height: 12, borderRadius: "50%", bgcolor: "error.main" }} />
              <Typography variant="caption" fontSize= "16px" fontWeight="500">Filled</Typography>
            </Box>
          </Box>

          <Grid container spacing={2}>
            {buildingData.map((building, index) => (
              <Grid item xs={12} sm={6} md={3} lg={1.7} key={building.buildingId}>
                <Box>
                  <Box 
                    sx={{ height: 200, display: "flex", flexDirection: "column", borderRadius: 1, overflow: "hidden", border: "1px solid", borderColor: "grey.300", cursor: "pointer" }}
                    onMouseEnter={(e) => handlePopoverOpen(e, building)}
                    onMouseLeave={handlePopoverClose}
                  >
                    <Box sx={{ flex: building.availableBins, bgcolor: "success.main" }} />
                    <Box sx={{ flex: building.occupiedBins, bgcolor: "error.main" }} />
                  </Box>
                  <Typography variant="caption" sx={{ display: "block", textAlign: "center", mt: 1, fontSize: "13px", fontWeight:"700" }}>
                    {building.buildingName}
                  </Typography>
                </Box>
              </Grid>
            ))}
          </Grid>
          </Paper>
        </Box>
        </Box>
      </Paper>

      <Popover
        sx={{
          pointerEvents: 'none',
        }}
        open={open && selectedBuilding !== null}
        anchorEl={anchorEl}
        anchorOrigin={{
          vertical: 'top',
          horizontal: 'center',
        }}
        transformOrigin={{
          vertical: 'bottom',
          horizontal: 'center',
        }}
        onClose={handlePopoverClose}
        disableRestoreFocus
      >
        <Box sx={{ p: 2, minWidth: 200 }}>
          <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1 }}>
            {selectedBuilding?.buildingName}
          </Typography>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.5 }}>
            <Box sx={{ width: 12, height: 12, borderRadius: "50%", bgcolor: "success.main" }} />
            <Typography variant="body2" color={"grey.500"}>Available Bins: {selectedBuilding?.availableBins}</Typography>
          </Box>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Box sx={{ width: 12, height: 12, borderRadius: "50%", bgcolor: "error.main" }} />
            <Typography variant="body2" color={"grey.500"}>Occupied Bins: {selectedBuilding?.occupiedBins}</Typography>
          </Box>
        </Box>
      </Popover>
    </Box>
  );
};
export default Dashboard;
